import V2Operate from '@/common/components/Others/V2Operate';
import React, { useMemo, useRef, useState } from 'react';
import { useMethods } from '@lhb/hook';
import { refactorPermissions, getJurisList, noop } from '@lhb/func';
import { getCookie } from '@lhb/cache';
import copy from 'copy-to-clipboard';
import dayjs from 'dayjs';
import V2Title from '@/common/components/Feedback/V2Title';
import ChangeStatusModal from '../../shop/components/ChangeStatusModal';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import ShopEditDrawer from '../../shopEdit/Drawer';
import AdaptiveTaskDetail from '@/common/components/business/ExpandStore/AdaptiveTaskDetail';
import styles from './index.module.less';
import { ShopEditType } from '../../shopEdit/ts-config';
import { getShareId } from '@/common/api/common';
import { getShopListColumn } from '@/common/api/expandStore/shop';

const targetBtnEvents = [ // 指定的按钮
  'standardShop:task',
  'standardShop:share',
  'standardShop:changeStatus',
  'standardShop:edit',
];

/** 门店详情-顶部（详情操作按钮） */
const DetailTitle: React.FC<{
   reload, detail, readonly?: boolean
}> = ({ reload, detail, readonly }) => {
  const editRef: any = useRef();
  const detailRef: any = useRef();
  const [statusVisible, setStatusVisible] = useState<boolean>(false); // 变更状态

  // https://confluence.lanhanba.com/pages/viewpage.action?pageId=119174088
  // UI要求的按钮排序 顺序由左往右 拓店任务，分享，变更状态，编辑（蓝色高亮）
  const operationBtns = {
    'task': true, // 拓店任务
    'share': true, // 分享
    'changeStatus': true, // 变更状态
    'statusRecord': true, // 场地下架
    'edit': { type: 'primary', }, // 编辑
  };

  const methods = useMethods({
    handleEdit() {
      editRef.current?.init({ id: detail.id, type: ShopEditType.Edit });
    },
    handleChangeStatus() {
      setStatusVisible(true);
    },
    handleShare() {
      const failureTime = dayjs().add(30, 'day').format('YYYY-MM-DD HH:mm:ss');
      const tenantId = getCookie('tenantId');
      const employeeId = getCookie('employeeId');
      return getShopListColumn({ tenantId, checked: 1 }).then((response: any) => {
        const config = Array.isArray(response) ? response : response?.data;
        const shopHeaderConfig = Array.isArray(config)
          ? config.filter((item: any) => item?.supportShare === true || item?.supportShare === 1)
          : [];
        return getShareId({
          longLinkUrl: `${process.env.INSIGHT_URL}/share/store`,
          content: JSON.stringify({ id: detail.id, shopId: detail.id, tenantId, employeeId, shopHeaderConfig }),
          expirationDate: failureTime,
          expirationRedirectUrl: `${process.env.INSIGHT_URL}/share/failure`,
          needToken: true,
        }).then((res: any) => {
          if (!res?.frontShareLinkUrl) return;
          copy(`${detail.name}：${res.frontShareLinkUrl}`);
          V2Message.success('链接已复制，可直接发送给你的朋友');
        }, noop).finally(noop);
      }, noop).finally(noop);
    },
    handleTask() {
      // 数据来源 1:系统生成 2:导入
      if (detail.origin === 2) {
        V2Message.warning('导入数据无拓店任务可查看');
        return;
      }
      (detailRef.current as any).handleTaskDetail(detail.taskId);
    },
  });

  const operateList = useMemo(() => {
    if (readonly) return [];

    // https://confluence.lanhanba.com/pages/viewpage.action?pageId=119174088
    // UI要求的按钮排序 顺序由左往右 拓店任务，分享，变更状态，编辑（蓝色高亮）
    const { permissions } = detail || {};
    let sortPermissions: any[] = [];
    const taskBtn = permissions?.find((item) => item.event === 'standardShop:task');
    taskBtn && sortPermissions.push(taskBtn);
    const shareBtn = permissions?.find((item) => item.event === 'standardShop:share');
    shareBtn && sortPermissions.push(shareBtn);
    const changeStatusBtn = permissions?.find((item) => item.event === 'standardShop:changeStatus');
    changeStatusBtn && sortPermissions.push(changeStatusBtn);
    const editBtn = permissions?.find((item) => item.event === 'standardShop:edit');
    editBtn && sortPermissions.push(editBtn);
    // 如果还有其他的按钮，那就放前面
    const otherBtn = permissions?.filter((item) => !targetBtnEvents.includes(item.event));
    otherBtn?.length > 0 && (sortPermissions = otherBtn.concat(sortPermissions));
    return getJurisList(operationBtns, refactorPermissions(sortPermissions), methods);
  }, [detail.permissions, methods, readonly]);

  return (
    <>
      <V2Title
        type='H1'
        text='门店详情'
        extra={
          <V2Operate operateList={operateList} />
        }
        className={styles.shopDetailTitle}
      />

      {/* 编辑 */}
      <ShopEditDrawer ref={editRef} reload={reload}/>

      {/* 变更状态 */}
      <ChangeStatusModal visible={statusVisible} setVisible={setStatusVisible} record={detail} onRefresh={reload}/>

      {/* 拓店任务 */}
      <AdaptiveTaskDetail ref={detailRef} reload={reload}/>
    </>
  );
};

export default DetailTitle;
